import { useEffect, useState } from 'react';
import { useAuth } from '../../auth';
import type { AccountDeletionRequest } from '@modelcast/types';
import {
  canSubmitAnotherAccountDeletionRequest,
  isDeletionCompleted,
  isDeletionScheduled,
} from './accountDeletionPanelState';

type RequestStep = 'form' | 'confirm';

function formatDate(value: string | null) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function statusLabel(request: AccountDeletionRequest) {
  if (request.status === 'pending') return 'Pending review';
  if (isDeletionCompleted(request)) return 'Deletion completed';
  if (isDeletionScheduled(request)) return 'Approved — deletion scheduled';
  if (request.status === 'rejected') return 'Rejected';
  return 'Unknown status';
}

function statusClass(request: AccountDeletionRequest) {
  if (request.status === 'pending') return 'bg-warn/20 text-warn';
  if (request.status === 'approved') return 'bg-ok/20 text-ok';
  if (request.status === 'rejected') return 'bg-red-500/20 text-red-500';
  return 'bg-panel2 text-text-dim';
}

export function AccountDeletionPanel() {
  const { getToken } = useAuth();
  const [request, setRequest] = useState<AccountDeletionRequest | null>(null);
  const [requestNote, setRequestNote] = useState('');
  const [step, setStep] = useState<RequestStep>('form');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchRequest();
  }, []);

  async function fetchRequest() {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch('/api/account-deletion/me', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Failed to load account deletion status');
      }
      const data = await response.json();
      setRequest(data.request ?? null);
    } catch (err) {
      console.error('Error loading account deletion request:', err);
      setError(err instanceof Error ? err.message : 'Failed to load account deletion status');
    } finally {
      setIsLoading(false);
    }
  }

  async function submitRequest() {
    setIsSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch('/api/account-deletion/requests', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ requestNote: requestNote.trim() || null }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to request account deletion');
      }
      const data = await response.json();
      setRequest(data.request);
      setRequestNote('');
      setStep('form');
    } catch (err) {
      console.error('Error requesting account deletion:', err);
      setError(err instanceof Error ? err.message : 'Failed to request account deletion');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border border-line bg-panel p-5">
        <div className="text-sm text-text-dim">Loading account status...</div>
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="rounded-lg border border-line bg-panel p-5">
        <div className="text-sm text-red-700">{error}</div>
        <button type="button" onClick={fetchRequest} className="btn btn-sm mt-4">
          Try again
        </button>
      </div>
    );
  }

  const canSubmitAnotherRequest = canSubmitAnotherAccountDeletionRequest(request);

  if (request && !canSubmitAnotherRequest) {
    const requestedAt = formatDate(request.requestedAt);
    const resolvedAt = formatDate(request.resolvedAt);

    return (
      <div className="rounded-lg border border-line bg-panel p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium text-text">Account deletion</h2>
            <p className="mt-1 text-sm text-text-dim">
              Your latest account deletion request is recorded.
            </p>
          </div>
          <span className={`rounded px-2 py-1 text-xs font-medium ${statusClass(request)}`}>
            {statusLabel(request)}
          </span>
        </div>

        <dl className="mt-5 grid gap-3 text-sm">
          <div>
            <dt className="label-mono">Requested</dt>
            <dd className="mt-1 text-text">{requestedAt}</dd>
          </div>
          {resolvedAt && (
            <div>
              <dt className="label-mono">Reviewed</dt>
              <dd className="mt-1 text-text">{resolvedAt}</dd>
            </div>
          )}
          {request.executedAt && (
            <div>
              <dt className="label-mono">Completed</dt>
              <dd className="mt-1 text-text">{formatDate(request.executedAt)}</dd>
            </div>
          )}
        </dl>

        {request.status === 'pending' && (
          <p className="mt-5 text-sm text-text-dim">
            Your request is pending admin review. You can still sign in while it is pending. To withdraw it, contact support.
          </p>
        )}
        {isDeletionScheduled(request) && (
          <p className="mt-5 text-sm text-text-dim">
            Your request was approved and deletion is scheduled. An admin will anonymize your public profile, remove your name from session chat and artwork, and permanently delete your sign-in. You will receive an email when this completes. Financial records are retained for up to 7 years for tax and accounting law, decoupled from your public identity.
          </p>
        )}
        {isDeletionCompleted(request) && (
          <p className="mt-5 text-sm text-text-dim">
            Your account deletion is complete. Your public profile has been anonymized and your sign-in has been removed.
          </p>
        )}
        {request.status === 'rejected' && (
          <p className="mt-5 text-sm text-text-dim">
            Your request was reviewed and rejected. Contact support if you need this reviewed again.
          </p>
        )}
        {!['pending', 'approved', 'rejected'].includes(request.status) && (
          <p className="mt-5 text-sm text-text-dim">
            Your request status is unrecognized. Please contact support.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-line bg-panel p-5">
      <h2 className="text-lg font-medium text-text">Account deletion</h2>
      <p className="mt-1 text-sm text-text-dim">
        Request deletion of your account. Requests are reviewed by admins, and deletion or anonymization is handled manually after review.
      </p>

      {request && canSubmitAnotherRequest && (
        <div className="mt-5 rounded-lg border border-line bg-panel2/60 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-medium text-text">Previous request</h3>
              <p className="mt-1 text-sm text-text-dim">
                Latest status: {statusLabel(request)}.
              </p>
            </div>
            <span className={`rounded px-2 py-1 text-xs font-medium ${statusClass(request)}`}>
              {statusLabel(request)}
            </span>
          </div>
        </div>
      )}

      {step === 'form' ? (
        <div className="mt-5 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="account-deletion-note" className="label-mono">
              Note <span className="font-normal text-text-faint">(optional)</span>
            </label>
            <textarea
              id="account-deletion-note"
              value={requestNote}
              onChange={(event) => setRequestNote(event.target.value)}
              maxLength={2000}
              rows={4}
              className="input resize-none pt-3 text-[15px]"
              placeholder="Add context for the admin reviewing this request"
            />
          </div>
          <button
            type="button"
            onClick={() => setStep('confirm')}
            disabled={isSubmitting}
            className="btn w-fit disabled:opacity-50"
          >
            Request account deletion
          </button>
        </div>
      ) : (
        <div className="mt-5 rounded-lg border border-warn/40 bg-warn/10 p-4">
          <h3 className="text-sm font-medium text-text">Confirm deletion request</h3>
          <p className="mt-2 text-sm text-text-dim">
            This creates an admin-reviewed deletion request. It does not immediately delete your account, purchases, payouts, tax, fraud, or safety records.
          </p>
          {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setStep('form')}
              disabled={isSubmitting}
              className="btn disabled:opacity-50"
            >
              Back
            </button>
            <button
              type="button"
              onClick={submitRequest}
              disabled={isSubmitting}
              className="btn btn-accent disabled:opacity-50"
              aria-busy={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Confirm request'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
